import { cacheKey } from '@/constants/cache-key';
import {
	type IApiResponse,
	type ICreateElectionState,
	type IElectionPollingUnits,
	type IGetElectionByID,
	type IIRevElectionResponse,
	type IUpdateElectionState,
} from '@/helpers/types';
import { type IElectionState } from '@/interface/state/IElectionState';
import { getCommandQuery, getCoreQuery, type IGetQuery } from '@/store/actions/api-utils';
import {
	type ICreateElection,
	type IIRevElection,
	type IUpdateElection,
	type IUpdateIREVReference,
} from '@/store/actions/core/election';

import { coreApiSlice } from '../apiSlice';

export const electionApiSlice = coreApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getElection: builder.query<IApiResponse<IElectionState[]>, IGetQuery>({
			query: ({ paged, query }) => `/Election${getCoreQuery(paged, ...(query || []))}`,
		}),
		getElectionById: builder.query<IGetElectionByID, { id: string }>({
			query: ({ id }) => `/Election/${id}`,
		}),
		deleteElection: builder.mutation<IApiResponse, { id: string }>({
			query: ({ id }) => ({
				method: 'DELETE',
				url: `/Election/delete/${id}`,
			}),
		}),
		getIrevElection: builder.mutation<IIRevElectionResponse, IIRevElection[]>({
			query: (body) => ({
				method: 'POST',
				url: '/Irev/Election',
				body,
			}),
		}),
		createElection: builder.mutation<ICreateElectionState, ICreateElection[]>({
			query: (body) => ({
				method: 'POST',
				url: '/Election',
				body,
			}),
		}),
		getCustomElection: builder.mutation<IApiResponse, unknown>({
			query: (body) => ({
				method: 'POST',
				url: '/Election/ReturnCustumElectionData',
				body,
			}),
		}),
		getElectionByName: builder.mutation<IApiResponse, { electionNames: string[] }>({
			query: (body) => ({
				method: 'POST',
				url: '/Election/getElectionsByName',
				body,
			}),
		}),
		updateElection: builder.mutation<IUpdateElectionState, IUpdateElection>({
			query: (body) => ({
				method: 'PUT',
				url: '/Election/update',
				body,
			}),
		}),
		getElectionPollingUnits: builder.query<IElectionPollingUnits, IGetQuery>({
			query: ({ paged, query }) => `/Election/get-polling-units${getCommandQuery(paged, ...(query || []))}`,
		}),
		updateIREVReference: builder.mutation<IApiResponse, IUpdateIREVReference[]>({
			query: (body) => ({
				method: 'PUT',
				url: '/Election/update/irev-reference',
				body,
			}),
		}),
		getAllActiveElections: builder.query<IApiResponse<IElectionState[]>, void>({
			query: () => '/Election/active',
		}),
	}),
});

export const {
	useGetElectionQuery,
	useLazyGetElectionQuery,
	useGetElectionByIdQuery,
	useLazyGetElectionByIdQuery,
	useDeleteElectionMutation,
	useGetIrevElectionMutation,
	useCreateElectionMutation,
	useGetCustomElectionMutation,
	useGetElectionByNameMutation,
	useUpdateElectionMutation,
	useGetElectionPollingUnitsQuery,
	useLazyGetElectionPollingUnitsQuery,
	useUpdateIREVReferenceMutation,
	useGetAllActiveElectionsQuery,
	useLazyGetAllActiveElectionsQuery,
} = electionApiSlice;

export const selectGetElectionById = electionApiSlice.endpoints.getElectionById.select({ id: '' });
export const selectUpdateElection = electionApiSlice.endpoints.updateElection.select({
	fixedCacheKey: cacheKey.UPDATE_ELECTION,
	requestId: '',
});
export const selectGetElectionPU = electionApiSlice.endpoints.getElectionPollingUnits.select({});
export const selectGetIrevElection = electionApiSlice.endpoints.getIrevElection.select({
	fixedCacheKey: cacheKey.GETIREVELECTION,
	requestId: '',
});
